import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

/**
 * Actionable Follow-Up Questions parsing utilities.
 *
 * The backend appends a follow-up section to the end of an assistant answer using a
 * single section marker followed by prose with inline `<followup>…</followup>` tagged
 * questions interspersed:
 *
 *   Normal answer text...
 *
 *   ◈If you want I can <followup>Convert this to a FAQ</followup>, <followup>Add target personas</followup>, or <followup>Make the tone more formal</followup>
 *
 * There is no end marker — everything after the first `◈` is the follow-up payload and
 * is assumed to be the last thing in the message. The surrounding prose renders as
 * normal text, while each `<followup>…</followup>` renders as an inline clickable link.
 *
 * See LDR: https://hello.atlassian.net/wiki/spaces/AA6/pages/7164582669
 */

/** Section marker (U+25C8) that denotes the start of the follow-up questions payload. */
export const ACTIONABLE_FOLLOW_UPS_MARKER = '◈';

/** Status-node localId prefix used to render the follow-ups component inline. */
export const ACTIONABLE_FOLLOW_UPS_STATUS_PREFIX = 'actionable-follow-ups-';

/** Whether a status node localId belongs to an actionable follow-ups component. */
export const isActionableFollowUpsStatus = (localId?: string): boolean => {
	return localId != null && localId.startsWith(ACTIONABLE_FOLLOW_UPS_STATUS_PREFIX);
};

/** Match generic questions and Scheduled Chat boundaries, respecting quoted attributes and EOF. */
const FOLLOW_UP_SEGMENT_REGEX =
	/<\/?(followup)>|<\/?(scheduled_chat)\b(?:"[^"]*(?:"|$)|'[^']*(?:'|$)|[^'">])*(?:>|$)/g;
const SCHEDULED_CHAT_OPEN_TAG = '<scheduled_chat>';
const SCHEDULED_CHAT_CLOSE_TAG = '</scheduled_chat>';

const isPartialScheduledChatTag = (value: string): boolean => {
	return (
		!value.endsWith('>') &&
		(SCHEDULED_CHAT_OPEN_TAG.startsWith(value) || SCHEDULED_CHAT_CLOSE_TAG.startsWith(value))
	);
};

/** A run of surrounding prose that renders as normal text. */
export type FollowUpTextSegment = { type: 'text'; value: string };

/** A single follow-up question that renders as an inline clickable link. */
export type FollowUpQuestionSegment = { type: 'question'; value: string };

/** An ordered piece of the follow-up section: prose or a supported generic question. */
export type FollowUpSegment = FollowUpTextSegment | FollowUpQuestionSegment;

const GENERIC_FOLLOW_UP_REGEX = /<followup>([\s\S]*?)<\/followup>/g;

const parseGenericFollowUps = (payload: string): FollowUpSegment[] => {
	if (!payload) {
		return [];
	}

	// Strip a leading marker if the caller passed the full section including `◈`.
	const markerIndex = payload.indexOf(ACTIONABLE_FOLLOW_UPS_MARKER);
	let text =
		markerIndex === -1 ? payload : payload.slice(markerIndex + ACTIONABLE_FOLLOW_UPS_MARKER.length);

	// Drop an unterminated trailing `<followup>` fragment so a half-streamed question
	// is not rendered as raw text until its closing tag arrives.
	const lastOpen = text.lastIndexOf('<followup>');
	if (lastOpen !== -1 && text.indexOf('</followup>', lastOpen) === -1) {
		text = text.slice(0, lastOpen);
	}

	const segments: FollowUpSegment[] = [];
	let lastIndex = 0;

	const pushText = (value: string) => {
		if (value) {
			segments.push({ type: 'text', value });
		}
	};

	for (const match of text.matchAll(GENERIC_FOLLOW_UP_REGEX)) {
		const matchIndex = match.index ?? 0;
		// Prose preceding this question (preserved verbatim).
		pushText(text.slice(lastIndex, matchIndex));
		lastIndex = matchIndex + match[0].length;

		const question = match[1]?.trim();
		if (question) {
			segments.push({ type: 'question', value: question });
		}
	}

	// Trailing prose after the final question.
	pushText(text.slice(lastIndex));

	return segments;
};

/**
 * Parses the raw follow-up payload (the text that follows the `◈` marker) into an ordered
 * list of prose and supported generic questions, suppressing Scheduled Chat tags.
 *
 * The surrounding prose is preserved verbatim (only the `<followup>`/`</followup>` tags are
 * removed) so it can be rendered as normal stream text, with each question rendered inline.
 *
 * Malformed input is handled gracefully: empty/whitespace-only questions are dropped, and an
 * unterminated trailing `<followup>` fragment (an incomplete stream) is omitted entirely so a
 * half-streamed question never flashes as raw text.
 *
 * @param payload the text after the `◈` marker (may include the marker itself).
 */
export const parseActionableFollowUps = (payload: string): FollowUpSegment[] => {
	if (!isExperimentEnabled('rovo_chat_scheduled_chat_follow_up')) {
		return parseGenericFollowUps(payload);
	}
	if (!payload) {
		return [];
	}

	// A marker inside tag contents or attributes is data, not a section boundary.
	const markerIndex = payload.indexOf(ACTIONABLE_FOLLOW_UPS_MARKER);
	const firstTagIndex = payload.indexOf('<');
	let text =
		markerIndex !== -1 && (firstTagIndex === -1 || markerIndex < firstTagIndex)
			? payload.slice(markerIndex + ACTIONABLE_FOLLOW_UPS_MARKER.length)
			: payload;
	const partialTagStart = text.lastIndexOf('<');
	if (partialTagStart !== -1 && isPartialScheduledChatTag(text.slice(partialTagStart))) {
		text = text.slice(0, partialTagStart);
	}

	const segments: FollowUpSegment[] = [];
	const openTags: string[] = [];
	let lastIndex = 0;
	let captureTag = '';
	let contentStart = 0;
	let isCaptureValid = false;
	const pushText = (value: string) => {
		if (value) {
			segments.push({ type: 'text', value });
		}
	};

	for (const match of text.matchAll(FOLLOW_UP_SEGMENT_REGEX)) {
		const matchIndex = match.index ?? 0;
		const boundary = match[0];
		const boundaryEnd = matchIndex + boundary.length;
		const tag = match[1] ?? match[2];
		if (openTags.length === 0) {
			pushText(text.slice(lastIndex, matchIndex));
		}

		if (boundary.startsWith('</')) {
			if (openTags.length > 0) {
				if (openTags.at(-1) === tag) {
					openTags.pop();
				} else {
					isCaptureValid = false;
					// Resynchronize at the outer closing tag after malformed nesting.
					if (tag === captureTag) {
						openTags.length = 0;
					}
				}
				if (openTags.length === 0 && isCaptureValid && boundary === `</${captureTag}>`) {
					const value = text.slice(contentStart, matchIndex).trim();
					if (captureTag === 'followup') {
						const questionTagStart = value.lastIndexOf('<');
						if (
							value &&
							!/<\/?scheduled_chat\b/.test(value) &&
							(questionTagStart === -1 || !isPartialScheduledChatTag(value.slice(questionTagStart)))
						) {
							segments.push({ type: 'question', value });
						}
					}
				}
			}
		} else {
			if (openTags.length === 0) {
				captureTag = tag;
				contentStart = boundaryEnd;
				isCaptureValid = boundary === `<${tag}>`;
			} else {
				isCaptureValid = false;
			}
			if (!boundary.endsWith('/>')) {
				openTags.push(tag);
			}
		}
		lastIndex = boundaryEnd;
	}
	if (openTags.length === 0) {
		pushText(text.slice(lastIndex));
	}
	return segments;
};
